import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Elements } from "@stripe/react-stripe-js";
import CheckoutForm from "./CheckoutForm";
import { loadStripe } from "@stripe/stripe-js";
import Cookies from "js-cookie";
import config from "../Config";
import PaymentMethods from "./PaymentMethods";
import { HandleResponse } from "../functions/HandleResponse";
import { authFetch } from "../functions/authFetch";
import { useSingleFlight } from "../hooks/useSingleFlight";
import './pages.css';

const QUICK_AMOUNTS = [25, 50, 100, 200];

function Payment() {
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState("");
  const [balance, setBalance] = useState(0);
  const [showPaymentMethod, setShowPaymentMethod] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [creditAmount, setCreditAmount] = useState(null);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const runIntent = useSingleFlight();

  useEffect(() => {
    let cancelled = false;
    const token = Cookies.get("runner_app");

    const initializePaymentFlow = async () => {
      try {
        const responsePublicKey = await authFetch(`${config.AUTH_API}/v1/get-public-key`, {
          method: "GET",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        await HandleResponse(responsePublicKey, navigate);
        const { publishableKey } = await responsePublicKey.json();
        if (cancelled) return;
        setStripePromise(loadStripe(publishableKey));

        const responseBalance = await authFetch(`${config.AUTH_API}/v1/get-credit`, {
          method: "GET",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        await HandleResponse(responseBalance, navigate);
        const dataBalance = await responseBalance.json();
        if (!cancelled) setBalance(dataBalance.balance);
      } catch (err) {
        if (!cancelled) console.error("Error during initialization:", err);
      }
    };

    initializePaymentFlow();
    return () => { cancelled = true; };
  }, [navigate]);

  const startIntent = () => {
    runIntent(async () => {
      const token = Cookies.get("runner_app");
      const res = await authFetch(
        `${config.AUTH_API}/v1/create-payment-intent?amount=${creditAmount}&currency=usd`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        }
      );
      await HandleResponse(res, navigate);
      const { clientSecret } = await res.json();
      setClientSecret(clientSecret);
    });
  };

  const handleAmountInput = (e) => {
    const v = e.target.value;
    if (/^\d*\.?\d{0,2}$/.test(v)) { setInputValue(v); setError(''); }
  };

  const handleSubmit = () => {
    const amount = parseFloat(inputValue);
    if (isNaN(amount) || amount <= 3) {
      setError('Please enter an amount greater than $3.00');
      setShowErrorPopup(true);
      return;
    }
    setCreditAmount(amount * 100);
    setShowPaymentMethod(true);
    setError('');
    setShowErrorPopup(false);
  };

  const formattedBalance = `$${(balance / 100).toFixed(2)}`;

  return (
    <div className="bill-page">

      {/* ── Balance card ── */}
      <div className="bill-balance-card">
        <span className="bill-balance-eyebrow">Account Credit</span>
        <span className="bill-balance-amount">{formattedBalance}</span>
        <span className="bill-balance-sub">Available to spend on job executions</span>
      </div>

      {showPaymentMethod ? (
        /* ── Payment method selection ── */
        <div className="bill-card">
          <h2 className="bill-card-title">Select Payment Method</h2>
          <p className="bill-card-sub">
            Charging <strong>${(creditAmount / 100).toFixed(2)}</strong> to your account.
          </p>

          <PaymentMethods amount={creditAmount} />

          <button className="bill-new-method-btn" onClick={startIntent}>
            + Add New Card
          </button>

          {clientSecret && stripePromise && (
            <div className="bill-stripe-wrap">
              <Elements stripe={stripePromise} options={{ clientSecret }}>
                <CheckoutForm />
              </Elements>
            </div>
          )}

          <button className="bill-back-btn" onClick={() => { setShowPaymentMethod(false); setClientSecret(''); }}>
            ← Back
          </button>
        </div>
      ) : (
        /* ── Top-up form ── */
        <div className="bill-card">
          <h2 className="bill-card-title">Add Credit</h2>
          <p className="bill-card-sub">
            Credits are consumed as you run jobs. Add funds to keep your account active.
          </p>

          <div className="bill-amount-field">
            <span className="bill-amount-prefix">$</span>
            <input
              type="text"
              value={inputValue}
              onChange={handleAmountInput}
              placeholder="0.00"
              className="bill-amount-input"
            />
          </div>

          <div className="bill-quick-row">
            {QUICK_AMOUNTS.map(amt => (
              <button
                key={amt}
                className={`bill-quick-btn ${inputValue === String(amt) ? 'bill-quick-btn--active' : ''}`}
                onClick={() => { setInputValue(String(amt)); setError(''); }}
              >
                ${amt}
              </button>
            ))}
          </div>

          <button className="bill-submit-btn" onClick={handleSubmit}>
            Continue to Payment
          </button>

          <p className="bill-footnote">
            Payments are processed securely via Stripe. Credits never expire.
          </p>
        </div>
      )}

      {/* ── Error modal ── */}
      {showErrorPopup && (
        <div className="bill-modal-overlay" onClick={() => setShowErrorPopup(false)}>
          <div className="bill-modal" onClick={e => e.stopPropagation()}>
            <div className="bill-modal-icon">⚠</div>
            <h3 className="bill-modal-title">Invalid Amount</h3>
            <p className="bill-modal-body">{error}</p>
            <button className="bill-modal-btn" onClick={() => setShowErrorPopup(false)}>
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Payment;
